import Decimal from 'decimal.js';
import { forIn, replace } from 'lodash';
import { queryPersistence } from '@kc/gbiz-base/lib/QueryPersistence';
import qs from 'query-string';

export const dropZero = (str) => {
  if (!str) return '-';
  const e = new Decimal(str);
  return e.toFixed();
};

/**
 * 增加千分位分隔符
 * @param n
 * @returns {string}
 */
const SeparateNumberPool = {
  pool: Object.create(null),
  poolCount: 0,
  has(k) {
    return !!this.pool[k];
  },
  get(k) {
    return this.pool[k];
  },
  set(k, v) {
    if (this.poolCount > 100000) {
      // 清理缓存
      this.poolCount = 0;
      this.pool = Object.create(null);
    }
    if (!this.has(k)) {
      this.poolCount += 1;
    }
    this.pool[k] = v;
  },
};
export const separateNumber = (n) => {
  if (typeof +n !== 'number') {
    return n;
  }
  const num = `${n}`;

  if (SeparateNumberPool.has(num)) {
    return SeparateNumberPool.get(num);
  }
  if (!/^[0-9.]+$/.test(num)) {
    return n;
  }

  let integer = num;
  let floater = '';
  if (num.indexOf('.') > -1) {
    const arr = num.split('.');
    [integer, floater] = arr;
  }
  const len = integer.length;
  let parser = '';
  if (len > 3) {
    let count = 0;
    for (let i = len - 1; i >= 0; i -= 1) {
      parser = integer[i] + parser;
      count += 1;
      if (count % 3 === 0 && i > 0) {
        parser = `,${parser}`;
      }
    }
  } else {
    parser = integer;
  }
  if (floater !== '') {
    floater = `.${floater}`;
  }
  const r = `${parser}${floater}`;
  SeparateNumberPool.set(num, r);

  return r;
};

export const formatNumber = (num, precision) => {
  if (typeof +num !== 'number' || Number.isNaN(+num)) {
    return num;
  }
  return separateNumber(dropZero(numberFixed(num, precision)));
};

/**
 * @description 高精度乘法并取给定位数向下取整
 * @param a
 * @param b
 * @param decimal
 * @param round
 * @returns {string|*}
 */
export const multiplyFloor = (a, b, decimal = 8, round = Decimal.ROUND_FLOOR) => {
  return new Decimal(a).mul(b).toFixed(decimal, round);
};

/**
 * 高精度指定位数
 * @param v
 * @param decimal
 * @param round
 * @returns {*}
 */
export const numberFixed = (v, decimal, round = Decimal.ROUND_DOWN) => {
  const numberV = +v;
  if (typeof numberV !== 'number' || v === undefined) {
    return v;
  }
  if (numberV === 0) {
    return '0';
  }
  const stringV = v.toString(); // 防止数值超过最大范围，导致转换不准确
  return new Decimal(stringV).toFixed(decimal, round);
};

const removeLangQuery = (url = '') => {
  if (!url) {
    return url;
  }
  const langIndex = url.indexOf('lang=');
  if (langIndex !== -1) {
    const query = url.substr(url.indexOf('?') + 1);
    const queryArr = query.split('&') || [];
    if (queryArr && queryArr.length === 1) {
      if (queryArr[0].indexOf('lang=') === 0) {
        return url.substr(0, url.indexOf('?'));
      }
      return url;
    }
    let langQueryLen = 0;
    queryArr.forEach((item) => {
      if (item?.indexOf('lang=') === 0) {
        langQueryLen = item.length;
      }
    });
    // 有多个参数
    if (url[langIndex - 1] === '?') {
      return `${url.substr(0, url.indexOf('?') + 1)}${url.slice(url.indexOf('&') + 1)}`;
    }
    const delIndex = url.indexOf('&lang=');
    return `${url.substr(0, delIndex)}${url.substr(delIndex + langQueryLen + 1, url.length)}`;
  }
  return url;
};

export const changLangToPath = (lang, _href) => {
  if (lang && _href) {
    let base = '';
    if (lang === 'zh_HK') {
      base = 'zh-hant';
    } else if (lang === 'zh_CN') {
      base = 'zh-hans';
    } else {
      base = lang.split('_')[0];
    }
    const origin = window.location.origin;
    let _url = new URL(_href.match(/^https?/) ? _href : `${origin}${_href}`);
    // 不需要语言子路径的路由
    const withOutLangPath = ['/cert'];
    const withOutLang = withOutLangPath.some((item) => {
      return _url.pathname === item;
    });
    if (withOutLang) {
      return _href;
    }
    // 语言子路径项目的域名
    const hasLangPathDomain = window.LANG_DOMAIN || [
      'www.kucoin.',
      'trade.kucoin.',
      'futures.kucoin.',
      'm.kucoin.',
    ];
    const accord = hasLangPathDomain.some((item) => _url.hostname.startsWith(item));
    if (base && accord) {
      const homePage = _url.pathname && _url.pathname === `/${base}`;
      if (base !== 'en' && !(_url.pathname && _url.pathname.startsWith(`/${base}/`)) && !homePage) {
        if (_url.pathname === '/') {
          _url.pathname = `/${base}`;
        } else {
          _url.pathname = `/${base}${_url.pathname}`;
        }
      }
      return removeLangQuery(_url.toString());
    }
  }
  return _href;
};

/*
 * 处理html字符串里的host:
 * 1、html中用$your_host_name$占位，如： $TRADE_HOST$
 * 2、hostConfig传入 { your_host_name: 'xxx' }, 如： { TRADE_HOST: 'https://trade.kucoin.com' }
 */
export const replaceHost = (html, lang, hostConfig = {}) => {
  let result = html || '';
  forIn(hostConfig, (value, key) => {
    // eslint-disable-next-line
    const reg = new RegExp(`(\\\$${key}\\\$)(.*?)(('|"))`, 'g');
    result = replace(result, reg, (match, p1, p2, p3) => {
      const { query: oriQuery, ...parsedUrl } = qs.parseUrl(`${value}${p2}`, {
        parseFragmentIdentifier: true,
      });
      let query = {};
      if (!oriQuery) {
        query.lang = lang;
      } else {
        query = {
          ...oriQuery,
          lang,
        };
      }
      const url = queryPersistence.formatUrlWithStore(
        qs.stringifyUrl({
          query,
          ...parsedUrl,
        }),
      );
      const urlWithLang = changLangToPath(lang, url);
      return `${urlWithLang}${p3}`;
    });
  });
  return result;
};

/**
 * @decription 高精度加法
 */
export const add = (a, b) => {
  return new Decimal(a).plus(b);
};
